import { Grid2 } from '@mui/material';
import ButtonComponent from 'components/ButtonComponent';
import ImageUploaderComponent from 'components/ImageUploaderComponent';
import ModalComponent from 'components/ModalComponent';
import TextAreaComponentV2 from 'components/TextAreaComponentV2';
import TextFieldComponent from 'components/TextFieldComponent';
import { AppContext } from 'context/AppContextProvider';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import {
  useCreateCommunicationAsync,
  useUpdateCommunicationAsync,
} from 'services/CommunicationService/hooks/useMutation';
import {
  ICommunicationCreateProps,
  ICommunicationProps,
  ICommunicationUpdateProps,
} from 'services/CommunicationService/types';
import {
  transformHtmlToPlainText,
  transformPlainTextToHtml,
} from 'utils/TransformUtils';
import { CommunicationFormSchema } from '../../../services/CommunicationService/schemas';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import DatePickerComponent from '../../../components/DatePickerComponent';
import zod from 'zod';

interface ICommunicationEditModalProps {
  isOpen: boolean;
  communicationToEdit?: ICommunicationProps;
  handleSaveForm?: () => void;
  handleClose: () => void;
}

export type CommunicationFormInput = zod.infer<typeof CommunicationFormSchema>;

const CommunicationEditModal = ({
  isOpen,
  handleClose,
  communicationToEdit,
  handleSaveForm,
}: ICommunicationEditModalProps) => {
  const { control, reset, handleSubmit } = useForm<CommunicationFormInput>({
    resolver: zodResolver(CommunicationFormSchema),
    defaultValues: {
      name: '',
      description: '',
      initialDate: '',
      validUntil: '',
      image: '',
    },
  });
  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);
  const [selectedImage, setSelectedImage] = useState<Blob | string>();
  const {
    mutateAsync: mutateCreateCommunication,
    isLoading: isLoadingCreateCommunication,
  } = useCreateCommunicationAsync();
  const {
    mutateAsync: mutateUpdateCommunication,
    isLoading: isLoadingUpdateCommunication,
  } = useUpdateCommunicationAsync();

  const normalizeFormData = async (data: CommunicationFormInput) => {
    const encodedImage = await handleSelectedImage(selectedImage as string);
    if (
      encodedImage &&
      !encodedImage.startsWith('https://firebasestorage.googleapis.com/')
    ) {
      data.image = encodedImage;
    } else {
      data.image = '';
    }

    data.description = transformHtmlToPlainText(data.description);
  };

  const handleSave = async (data: CommunicationFormInput) => {
    setIsShowLoadingComponent(true);
    if (!selectedImage) {
      showSnackbar('Uma imagem deve ser vinculada ao comunicado.', 'error');
      return;
    } else {
      await normalizeFormData(data);
    }

    if (data.id) {
      await mutateUpdateCommunication({
        id: data.id,
        data: data as ICommunicationUpdateProps,
      })
        .then(() => {
          resetAllAndShowMessage('Comunicado atualizado com sucesso!');
        })
        .finally(() => {
          setIsShowLoadingComponent(false);
        });
    } else {
      await mutateCreateCommunication(data as ICommunicationCreateProps)
        .then(() => {
          resetAllAndShowMessage('Comunicado criado com sucesso!');
        })
        .finally(() => {
          setIsShowLoadingComponent(false);
        });
    }
  };

  const resetAllAndShowMessage = (message: string) => {
    setSelectedImage('');
    reset();
    if (handleSaveForm) handleSaveForm();
    showSnackbar(message, 'success');
    handleClose();
  };

  const handleSelectedImage = (image: string | Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (image instanceof Blob) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(image);
      } else {
        resolve(image);
      }
    });
  };

  const onChangeSelectedImage = (file: File) => {
    const blob = new Blob([file], { type: file.type });
    setSelectedImage(blob);
  };

  const urlToFile = async (url: string, fileName: string, mimeType: string) => {
    const response = await fetch(url, {
      method: 'GET',
      mode: 'no-cors',
    });
    const blob = await response.blob();
    return new File([blob], fileName, { type: mimeType });
  };

  const mapCommunicationToForm = (
    data: ICommunicationProps,
  ): CommunicationFormInput => {
    const initialDateStr = dayjs(data.initialDate).toISOString();
    const validUntilStr = dayjs(data.validUntil).toISOString();

    return {
      id: data.id,
      name: data.name,
      description: data.description,
      initialDate: initialDateStr,
      validUntil: validUntilStr,
      image: data.image,
    };
  };

  useEffect(() => {
    if (communicationToEdit && communicationToEdit.id) {
      const formValues = mapCommunicationToForm(communicationToEdit);
      reset(formValues, { keepDefaultValues: true });
      urlToFile(communicationToEdit.image, 'imagem.jpeg', 'image/jpeg').then(
          (response) => { 
            setSelectedImage(response as Blob);
        },
      );
    } else reset();
  }, [communicationToEdit]);

  return (
    <ModalComponent
      title={communicationToEdit ? 'Atualizar Comunicado' : 'Criar Comunicado'}
      onClose={handleClose}
      isOpen={isOpen}
    >
      <Grid2 container>
        <Grid2 size={12}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Nome"
                onChangeText={onChange}
                type={'text'}
                errorMessage={error?.message}
                value={value}
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>

        <Grid2 size={12}>
          <ImageUploaderComponent
            onDrop={(files) => onChangeSelectedImage(files[0])}
          />
        </Grid2>

        <Grid2 container size={12} spacing={2} sx={{ mt: 2 }}>
          <Grid2 size={6}>
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <DatePickerComponent
                  value={value ? dayjs(value).utc() : null}
                  onChange={(value) => {
                    const date = dayjs(value);
                    onChange(
                      date.isValid() ? dayjs(value).utc().toISOString() : null,
                    );
                  }}
                  errorMessage={error?.message}
                  label="Início"
                  disablePast={true}
                />
              )}
              name={'initialDate'}
              control={control}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <DatePickerComponent
                  value={value ? dayjs(value).utc() : null}
                  onChange={(value) => {
                    const date = dayjs(value);
                    onChange(
                      date.isValid() ? dayjs(value).utc().toISOString() : null,
                    );
                  }}
                  errorMessage={error?.message}
                  label="Validade"
                  disablePast={true}
                />
              )}
              name={'validUntil'}
              control={control}
            />
          </Grid2>
        </Grid2>

        <Grid2 size={12} sx={{ mt: 2 }}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <TextAreaComponentV2
                label="Descrição da imagem"
                value={transformPlainTextToHtml(value)}
                onChangeVal={onChange}
              />
            )}
            name={'description'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12}>
          <ButtonComponent
            variant="contained"
            color="primary"
            fullWidth
            loading={
              isLoadingCreateCommunication || isLoadingUpdateCommunication
            }
            sx={{ mt: 5 }}
            onClick={handleSubmit(handleSave)}
          >
            Salvar
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </ModalComponent>
  );
};

export default CommunicationEditModal;
